/**
 * @module i18n/en
 * @description English translations
 */

import type { Translations } from './types';

export const en: Translations = {
  common: {
    appName: 'refero',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    creating: 'Creating',
    close: 'Close',
    done: 'Done',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    back: 'Back',
    next: 'Next',
    search: 'Search',
    clear: 'Clear',
    refresh: 'Refresh',
    copy: 'Copy',
    duplicate: 'Duplicate',
    export: 'Export',
    import: 'Import',
    settings: 'Settings',
    help: 'Help',
    logout: 'Logout',
    noResults: 'No results found',
    required: 'Required',
    optional: 'Optional',
    retry: 'Retry',
  },

  auth: {
    title: 'Authentication',
    apiKeyLabel: 'Zotero API Key',
    apiKeyPlaceholder: 'Enter your Zotero API key',
    apiKeyHelp: 'You can find your API key in your Zotero settings at zotero.org/settings/keys',
    apiKeyInvalid: 'Invalid API key',
    login: 'Login',
    loggingIn: 'Logging in...',
    loginSuccess: 'Successfully logged in',
    loginFailed: 'Login failed',
    logoutSuccess: 'Successfully logged out',
    selectLibrary: 'Select Library',
    selectLibraryHelp: 'Choose which library you want to work with',
    myLibrary: 'My Library',
    groupLibraries: 'Group Libraries',
    noGroups: 'No group libraries available',
  },

  library: {
    title: 'Library',
    myLibrary: 'My Library',
    groups: 'Groups',
    noGroups: 'No groups',
    groupLibraries: 'Shared Libraries',
    selectLibrary: 'Select a library',
    librarySelected: 'Library selected',
    loadingLibraries: 'Loading libraries...',
    loadingGroups: 'Loading groups...',
    errorLoadingGroups: 'Failed to load groups',
  },

  collection: {
    title: 'Collection',
    collections: 'Collections',
    allItems: 'All Items',
    unfiled: 'Unfiled Items',
    trash: 'Trash',
    duplicates: 'Duplicate Items',
    publications: 'My Publications',
    newCollection: 'New Collection',
    newSubcollection: 'New Subcollection',
    renameCollection: 'Rename Collection',
    deleteCollection: 'Delete Collection',
    collectionName: 'Collection Name',
    collectionNamePlaceholder: 'Enter collection name',
    collectionCreated: 'Collection created',
    collectionRenamed: 'Collection renamed',
    collectionDeleted: 'Collection deleted',
    collectionEmpty: 'This collection is empty',
    confirmDelete: 'Delete Collection?',
    confirmDeleteMessage:
      'Are you sure you want to delete this collection? Items will not be deleted.',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    noCollections: 'No collections',
    loadingCollections: 'Loading collections...',
    errorLoadingCollections: 'Failed to load collections',
    emptyTrash: 'Empty Trash',
    emptyTrashConfirm: 'Are you sure you want to permanently delete all items in the trash?',
    trashEmptied: 'Trash emptied',
  },

  item: {
    title: 'Item',
    items: 'Items',
    item: 'item',
    newItem: 'New Item',
    selectType: 'Select item type',
    editItem: 'Edit Item',
    deleteItem: 'Delete Item',
    deleteItems: 'Delete Items',
    duplicateItem: 'Duplicate Item',
    itemCreated: 'Item created',
    itemUpdated: 'Item updated',
    itemDeleted: 'Item moved to trash',
    itemsDeleted: 'Items moved to trash',
    itemDuplicated: 'Item duplicated',
    confirmDelete: 'Delete Item?',
    confirmDeleteMessage: 'Are you sure you want to move this item to trash?',
    confirmDeleteMultiple: 'Are you sure you want to move {{count}} items to trash?',
    noItems: 'No items in this collection',
    loadingItems: 'Loading items...',
    errorLoadingItems: 'Failed to load items',
    // Item fields (basic)
    itemType: 'Item Type',
    title_field: 'Title',
    creators: 'Creators',
    date: 'Date',
    dateAdded: 'Date Added',
    dateModified: 'Date Modified',
    abstract: 'Abstract',
    publisher: 'Publisher',
    publicationTitle: 'Publication',
    volume: 'Volume',
    issue: 'Issue',
    pages: 'Pages',
    doi: 'DOI',
    isbn: 'ISBN',
    issn: 'ISSN',
    url: 'URL',
    accessDate: 'Access Date',
    language: 'Language',
    tags: 'Tags',
    notes: 'Notes',
    attachments: 'Attachments',
    // Creator management
    addCreator: 'Add creator',
    removeCreator: 'Remove',
    firstName: 'First name',
    lastName: 'Last name',
    // All field labels (from Zotero documentation)
    fields: {
      // General Fields
      title: 'Title',
      creators: 'Creators',
      date: 'Date',
      abstractNote: 'Abstract',
      language: 'Language',
      shortTitle: 'Short Title',
      url: 'URL',
      accessDate: 'Accessed',
      rights: 'Rights',
      extra: 'Extra',
      // Books and Periodicals
      publicationTitle: 'Publication',
      bookTitle: 'Book Title',
      dictionaryTitle: 'Dictionary Title',
      encyclopediaTitle: 'Encyclopedia Title',
      volume: 'Volume',
      issue: 'Issue',
      pages: 'Pages',
      edition: 'Edition',
      series: 'Series',
      seriesNumber: 'Series Number',
      seriesTitle: 'Series Title',
      seriesText: 'Series Text',
      numberOfVolumes: '# of Volumes',
      numPages: '# of Pages',
      section: 'Section',
      place: 'Place',
      publisher: 'Publisher',
      journalAbbreviation: 'Journal Abbr',
      ISBN: 'ISBN',
      ISSN: 'ISSN',
      DOI: 'DOI',
      // Archives and Access
      archive: 'Archive',
      archiveLocation: 'Loc. in Archive',
      libraryCatalog: 'Library Catalog',
      callNumber: 'Call Number',
      // Reports and Theses
      reportType: 'Type',
      reportNumber: 'Report Number',
      institution: 'Institution',
      university: 'University',
      thesisType: 'Type',
      // Presentations and Conferences
      proceedingsTitle: 'Proceedings Title',
      conferenceName: 'Conference Name',
      meetingName: 'Meeting Name',
      presentationType: 'Type',
      // Recordings and Broadcasts
      videoRecordingFormat: 'Format',
      audioRecordingFormat: 'Format',
      audioFileType: 'File Type',
      runningTime: 'Running Time',
      programTitle: 'Program Title',
      episodeNumber: 'Episode Number',
      network: 'Network',
      label: 'Label',
      distributor: 'Distributor',
      studio: 'Studio',
      genre: 'Genre',
      // Images, Artwork, and Maps
      artworkMedium: 'Medium',
      artworkSize: 'Artwork Size',
      mapType: 'Type',
      scale: 'Scale',
      // Primary Sources
      medium: 'Medium',
      interviewMedium: 'Medium',
      manuscriptType: 'Type',
      letterType: 'Type',
      subject: 'Subject',
      // Websites
      websiteTitle: 'Website Title',
      websiteType: 'Website Type',
      blogTitle: 'Blog Title',
      forumTitle: 'Forum/Listserv Title',
      postType: 'Post Type',
      // Software
      versionNumber: 'Version',
      system: 'System',
      company: 'Company',
      programmingLanguage: 'Programming Language',
      // Dataset
      identifier: 'Identifier',
      repository: 'Repository',
      repositoryLocation: 'Repository Location',
      format: 'Format',
      citationKey: 'Citation Key',
      // Standard
      number: 'Number',
      organization: 'Organization',
      committee: 'Committee',
      type: 'Type',
      status: 'Status',
      // Legal: Legislation
      nameOfAct: 'Name of Act',
      billNumber: 'Bill Number',
      code: 'Code',
      codeVolume: 'Code Volume',
      codeNumber: 'Code Number',
      publicLawNumber: 'Public Law Number',
      dateEnacted: 'Date Enacted',
      codePages: 'Code Pages',
      legislativeBody: 'Legislative Body',
      session: 'Session',
      history: 'History',
      documentNumber: 'Document Number',
      // Legal: Cases
      caseName: 'Case Name',
      court: 'Court',
      dateDecided: 'Date Decided',
      docketNumber: 'Docket Number',
      reporter: 'Reporter',
      reporterVolume: 'Reporter Volume',
      firstPage: 'First Page',
      // Legal: Patents
      country: 'Country',
      assignee: 'Assignee',
      issuingAuthority: 'Issuing Authority',
      patentNumber: 'Patent Number',
      filingDate: 'Filing Date',
      issueDate: 'Issue Date',
      applicationNumber: 'Application Number',
      priorityNumbers: 'Priority Numbers',
      references: 'References',
      legalStatus: 'Legal Status',
      // Extra/CSL fields
      originalDate: 'Original Date',
      originalTitle: 'Original Title',
      originalPublisher: 'Original Publisher',
      originalPublisherPlace: 'Original Publisher Place',
      PMID: 'PMID',
      PMCID: 'PMCID',
    },
    // Item types
    types: {
      book: 'Book',
      bookSection: 'Book Section',
      journalArticle: 'Journal Article',
      magazineArticle: 'Magazine Article',
      newspaperArticle: 'Newspaper Article',
      thesis: 'Thesis',
      report: 'Report',
      conferencePaper: 'Conference Paper',
      webpage: 'Web Page',
      document: 'Document',
      presentation: 'Presentation',
      patent: 'Patent',
      statute: 'Statute',
      case: 'Case',
      interview: 'Interview',
      film: 'Film',
      artwork: 'Artwork',
      podcast: 'Podcast',
      videoRecording: 'Video Recording',
      audioRecording: 'Audio Recording',
      encyclopediaArticle: 'Encyclopedia Article',
      dictionaryEntry: 'Dictionary Entry',
      manuscript: 'Manuscript',
      radioBroadcast: 'Radio Broadcast',
      tvBroadcast: 'TV Broadcast',
      blogPost: 'Blog Post',
      forumPost: 'Forum Post',
      letter: 'Letter',
      email: 'Email',
      instantMessage: 'Instant Message',
      map: 'Map',
      dataset: 'Dataset',
      standard: 'Standard',
      computerProgram: 'Software',
      bill: 'Bill',
      hearing: 'Hearing',
    },
    // Creator types
    creatorTypes: {
      author: 'Author',
      editor: 'Editor',
      translator: 'Translator',
      contributor: 'Contributor',
      bookAuthor: 'Book Author',
      seriesEditor: 'Series Editor',
      reviewedAuthor: 'Reviewed Author',
      director: 'Director',
      artist: 'Artist',
      performer: 'Performer',
      composer: 'Composer',
      interviewer: 'Interviewer',
      interviewee: 'Interview With',
      producer: 'Producer',
      presenter: 'Presenter',
      scriptwriter: 'Scriptwriter',
      wordsBy: 'Words By',
      castMember: 'Cast Member',
      guest: 'Guest',
      podcaster: 'Podcaster',
      cartographer: 'Cartographer',
      programmer: 'Programmer',
      recipient: 'Recipient',
      commenter: 'Commenter',
      sponsor: 'Sponsor',
      cosponsor: 'Cosponsor',
      counsel: 'Counsel',
      inventor: 'Inventor',
      attorneyAgent: 'Attorney/Agent',
    },
  },

  search: {
    placeholder: 'Search items...',
    searching: 'Searching...',
    noResults: 'No items match your search',
    resultsCount: '{{count}} results found',
    recentSearches: 'Recent Searches',
    clearHistory: 'Clear History',
    searchIn: 'Search in',
    searchAll: 'All Fields',
    searchTitles: 'Titles',
    searchCreators: 'Creators',
    searchTags: 'Tags',
  },

  sort: {
    sortBy: 'Sort by',
    ascending: 'Ascending',
    descending: 'Descending',
    title: 'Title',
    creator: 'Creator',
    date: 'Date',
    dateAdded: 'Date Added',
    dateModified: 'Date Modified',
    itemType: 'Item Type',
    year: 'Year',
    publisher: 'Publisher',
  },

  pagination: {
    page: 'Page',
    of: 'of',
    items: 'items',
    showing: 'Showing',
    first: 'First',
    previous: 'Previous',
    next: 'Next',
    last: 'Last',
    perPage: 'per page',
  },

  export: {
    title: 'Export',
    format: 'Format',
    bibtex: 'BibTeX',
    biblatex: 'BibLaTeX',
    ris: 'RIS',
    csljson: 'CSL JSON',
    mods: 'MODS',
    refer: 'Refer/BibIX',
    tei: 'TEI',
    wikipedia: 'Wikipedia',
    csv: 'CSV',
    exportSuccess: 'Export successful',
    exportFailed: 'Export failed',
    copyToClipboard: 'Copy to Clipboard',
    copiedToClipboard: 'Copied to clipboard',
    copyFailed: 'Failed to copy',
    copyTitle: 'Copy Title',
    copyUrl: 'Copy URL',
    copyCitation: 'Copy Citation',
    // New keys for export modal
    exportItems: 'Export References',
    exportItem: 'Export Reference',
    exportCollection: 'Export Collection',
    selectedItems: '{{count}} reference(s) selected',
    formatLabel: 'Export Format',
    styleLabel: 'Citation Style',
    bibliography: 'Formatted Bibliography',
    download: 'Download',
    preview: 'Preview',
    generating: 'Generating...',
    noItemsSelected: 'No references selected',
    selectItemsFirst: 'Please select references to export first',
  },

  detailsPanel: {
    title: 'Details',
    noItemSelected: 'No item selected',
    selectItemToView: 'Select an item to view its details',
    viewMode: 'View',
    editMode: 'Edit',
    saveChanges: 'Save Changes',
    discardChanges: 'Discard Changes',
    unsavedChanges: 'Unsaved Changes',
    confirmDiscard: 'Discard unsaved changes?',
    info: 'Info',
    notes: 'Notes',
    tags: 'Tags',
    related: 'Related',
  },

  errors: {
    generic: 'An error occurred',
    network: 'Network error. Please check your connection.',
    unauthorized: 'Unauthorized. Please log in again.',
    forbidden: 'You do not have permission to perform this action.',
    notFound: 'The requested resource was not found.',
    serverError: 'Server error. Please try again later.',
    timeout: 'Request timed out. Please try again.',
    rateLimited: 'Too many requests. Please wait and try again.',
    invalidApiKey: 'Invalid API key. Please check your credentials.',
    loadFailed: 'Failed to load data',
    saveFailed: 'Failed to save changes',
    deleteFailed: 'Failed to delete',
    connectionLost: 'Connection lost',
    tryAgain: 'Try Again',
  },

  settings: {
    title: 'Settings',
    language: 'Language',
    theme: 'Theme',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    autoTheme: 'System Default',
    panelSizes: 'Panel Sizes',
    resetPanels: 'Reset Panel Sizes',
    clearCache: 'Clear Cache',
    clearCacheConfirm: 'Are you sure you want to clear the cache?',
    cacheCleared: 'Cache cleared',
    about: 'About',
    version: 'Version',
    // Item types & fields preferences
    itemTypes: 'Item Types',
    fields: 'Fields',
    itemTypesDescription:
      'Select which item types you want to use. Disabled types will not appear in the "New Item" menu.',
    fieldsDescription:
      'Choose which fields to display for each item type. This simplifies the editing form.',
    selectItemType: 'Configure fields for',
    selectAll: 'Select All',
    selectCommon: 'Common Types',
    showAllFields: 'Show All',
    showEssential: 'Essential Only',
    resetToDefault: 'Reset',
    resetAll: 'Reset All Preferences',
    confirmReset: 'Are you sure you want to reset all preferences to defaults?',
    preferencesReset: 'Preferences reset to defaults',
  },

  a11y: {
    skipToContent: 'Skip to content',
    mainNavigation: 'Main navigation',
    collectionsPanel: 'Collections panel',
    itemsPanel: 'Items panel',
    detailsPanel: 'Details panel',
    closeModal: 'Close modal',
    expandCollection: 'Expand collection',
    collapseCollection: 'Collapse collection',
    selectItem: 'Select item',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    loading: 'Loading',
    sortAscending: 'Sort ascending',
    sortDescending: 'Sort descending',
    itemActions: 'Item actions',
    collectionActions: 'Collection actions',
  },

  scan: {
    title: 'Scan ISBN',
    scanButton: 'Scan',
    scanning: 'Scanning...',
    hint: 'Point your camera at an ISBN barcode',
    scannedISBN: 'Scanned ISBN',
    scanAnother: 'Scan another',
    cameraAccessDenied: 'Camera access denied. Please allow camera access to scan barcodes.',
    noCameraFound: 'No camera found on this device.',
    unsupportedBrowser: 'BarcodeDetector API not supported in this browser. Use Chrome or Edge.',
    // ISBN search
    searchingCatalogs: 'Searching catalogs...',
    foundIn: 'Found in',
    notFound: 'No record found in catalogs',
    searchedIn: 'Searched in',
  },

  import: {
    // General
    title: 'Import References',
    importButton: 'Import',
    // Tabs
    tabCatalogs: 'Bibliographic catalogs',
    tabFile: 'Import file',
    // Catalog search
    searchPlaceholder: 'Title, author, ISBN...',
    searchIndex: 'Search index',
    documentType: 'Document type',
    searching: 'Searching...',
    noResults: 'No results found',
    tryDifferentSearch: 'Try different search terms',
    welcomeMessage: 'Search for records to import',
    searchHint: 'Enter a title, author, ISBN or subject',
    enterSearchTerm: 'Please enter a search term',
    searchFailed: 'Search failed',
    resultsCount: '{{count}} result(s) found',
    selectAll: 'Select all',
    untitled: 'Untitled',
    selectRecordsFirst: 'Please select records to import first',
    noLibrarySelected: 'No library selected',
    importing: 'Importing...',
    importSelected: 'Import ({{count}})',
    importSuccess: '{{count}} record(s) imported successfully',
    importPartialFailed: '{{count}} record(s) could not be imported',
    // Source selection
    source: 'Source',
    sourceSudoc: 'Sudoc (French catalogs)',
    sourceBnf: 'BnF',
    sourceGoogleBooks: 'Google Books',
    sourceSwisscovery: 'Swisscovery',
    sourceOpenLibrary: 'Open Library',
    sourceHarvardLibrary: 'Harvard Library',
    sourceWikidata: 'Wikidata',
    sudocSearchHint: 'Search Sudoc (French academic catalogs)',
    googleBooksSearchHint: 'Search Google Books',
    harvardLibrarySearchHint: 'Search Harvard Library',
    bnfSearchHint: 'Search BnF (French National Library)',
    swisscoverySearchHint: 'Search Swisscovery (Swiss libraries)',
    openLibrarySearchHint: 'Search Open Library (Internet Archive)',
    wikidataSearchHint: 'Search Wikidata (free knowledge base)',
    addCriteria: 'Add criteria',
    // File import
    dropZoneText: 'Drag and drop a file here',
    dropZoneHint: 'or click to select a file',
    browseFiles: 'Browse...',
    supportedFormats: 'Supported formats',
    detectedFormat: 'Detected format',
    fileParseSuccess: '{{count}} reference(s) found in file',
    fileParseFailed: 'Unable to read file',
    recordsFound: '{{count}} reference(s) found',
  },
};

export default en;
